import React, { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  GizmoHelper,
  GizmoViewport,
  Environment,
  ContactShadows,
} from "@react-three/drei";
import * as THREE from "three";

/**
 * 3D visualization of a Weld-Neck flange in the field-controller "Spool"
 * mode.
 *
 * Renders (all to real proportions from the flange library):
 *   • Flange body (short thick disc, OD × thickness_wn)
 *   • Weld-neck hub tapering from just outside the pipe OD down to the
 *     pipe OD, extending behind the flange face
 *   • Raised face or RTJ groove on the front
 *   • Bolt holes drilled through at PCD with hex bolt heads
 *   • Per-face coverage rings that light up as measurements come in
 *     (amber below 180°, neon green above)
 *   • Measured points as glowing spheres, one color per face:
 *        face1 red · face2 amber · face3 cyan · bolt magenta
 *
 * Dimensions in the scene are normalized so OD radius = 1 unit.
 */
export const Flange3D = ({
  flange,
  activeKind = "face1",
  face1Points = [], face2Points = [], face3Points = [], boltPoints = [],
  face1Sectors = [], face2Sectors = [], face3Sectors = [], boltSectors = [],
  face1Coverage = 0, face2Coverage = 0, face3Coverage = 0, boltCoverage = 0,
}) => {
  if (!flange) {
    return (
      <div className="flex h-full min-h-[280px] items-center justify-center rounded-sm border border-[var(--border)] bg-[var(--surface-2)] text-[var(--text-mute)]">
        Select a flange to preview
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-[280px] w-full overflow-hidden rounded-sm border border-[var(--border)] bg-gradient-to-b from-[#1a2130] to-[#0a0f18]" data-testid="flange-3d">
      <Canvas
        camera={{ position: [2.4, 1.5, 2.6], fov: 38 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: false, toneMapping: THREE.ACESFilmicToneMapping }}
        shadows
      >
        <color attach="background" args={["#151b26"]} />

        {/* Lighting rig */}
        <ambientLight intensity={0.9} color="#c8d7f0" />
        <hemisphereLight args={["#e0e8ff", "#151b26", 0.8]} />
        <directionalLight
          position={[4, 5, 3]}
          intensity={1.6}
          castShadow
          shadow-mapSize={[1024, 1024]}
          color="#ffffff"
        />
        <directionalLight
          position={[-4, 2, -3]}
          intensity={0.9}
          color="#88c8ff"
        />
        <pointLight position={[0, 3, 0]} intensity={0.4} color="#ffffff" />
        <pointLight position={[0, -2, 3]} intensity={0.5} color="#00ff41" />

        {/* Environment for reflections */}
        <Environment preset="warehouse" background={false} />

        {/* Ground */}
        <ContactShadows
          position={[0, -0.5, 0]}
          opacity={0.55}
          scale={7}
          blur={2.6}
          far={2}
          color="#000"
        />
        <gridHelper
          args={[10, 40, "#2a3549", "#1a2130"]}
          position={[0, -0.499, 0]}
        />

        <FlangeMesh
          flange={flange}
          activeKind={activeKind}
          face1Points={face1Points} face2Points={face2Points}
          face3Points={face3Points} boltPoints={boltPoints}
          face1Sectors={face1Sectors} face2Sectors={face2Sectors}
          face3Sectors={face3Sectors} boltSectors={boltSectors}
          face1Coverage={face1Coverage} face2Coverage={face2Coverage}
          face3Coverage={face3Coverage} boltCoverage={boltCoverage}
        />

        <OrbitControls
          enablePan={false}
          minDistance={1.6}
          maxDistance={7}
          minPolarAngle={0.1}
          maxPolarAngle={Math.PI / 2 + 0.2}
          autoRotate
          autoRotateSpeed={0.5}
        />
        <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
          <GizmoViewport axisColors={["#ff5555", "#55ff77", "#5599ff"]} labelColor="black" />
        </GizmoHelper>
      </Canvas>

      {/* HUD overlay */}
      <div className="pointer-events-none absolute left-3 right-3 top-3 flex items-start justify-between text-[10px] uppercase tracking-widest">
        <div className="rounded-sm border border-[var(--border)] bg-black/60 px-2 py-1 backdrop-blur-sm">
          <div className="text-[var(--text-dim)]">
            {flange.standard} · {flange.size}" · cl.{flange.class} · {flange.gasket || "—"}
          </div>
          <div className="readout tabular-nums text-[11px] text-[var(--text)]">
            OD Ø{flange.od?.toFixed(1)}mm · PCD Ø{flange.pcd?.toFixed(1)}mm · {flange.no_bolt}×Ø{flange.dia_bolt?.toFixed(1)}mm
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <CoverageChip label="F1" active={activeKind === "face1"} cov={face1Coverage} n={face1Points.length} />
          <CoverageChip label="F2" active={activeKind === "face2"} cov={face2Coverage} n={face2Points.length} />
          <CoverageChip label="F3" active={activeKind === "face3"} cov={face3Coverage} n={face3Points.length} />
          <CoverageChip label="PCD" active={activeKind === "bolt"} cov={boltCoverage} n={boltPoints.length} />
        </div>
      </div>
    </div>
  );
};

const CoverageChip = ({ label, cov, n, active }) => {
  const ok = cov >= 180;
  const color = ok ? "#00ff41" : cov > 0 ? "#ffb000" : "#94a3b8";
  return (
    <div
      className="rounded-sm border bg-black/60 px-2 py-1 backdrop-blur-sm"
      style={{
        borderColor: active ? "#e2e8f0" : ok ? "#00ff41" : cov > 0 ? "#ffb000" : "#2a2f3a",
        borderWidth: active ? 2 : 1,
      }}
    >
      <span className="text-[10px] tracking-widest text-[var(--text-dim)]">{label}</span>{" "}
      <span className="readout tabular-nums" style={{ color }}>
        {Math.round(cov)}° · {n}pt
      </span>
    </div>
  );
};

// ---------- Inner mesh ----------

const KIND_COLORS = {
  face1: "#ff5555",
  face2: "#ffb000",
  face3: "#00e5ff",
  bolt:  "#ff55ff",
};

const FlangeMesh = ({
  flange, activeKind,
  face1Points, face2Points, face3Points, boltPoints,
  face1Sectors, face2Sectors, face3Sectors, boltSectors,
  face1Coverage, face2Coverage, face3Coverage, boltCoverage,
}) => {
  const group = useRef();

  // Real geometry ratios
  const od = flange.od || 200;
  const rfDia = flange.rf_dia || od * 0.65;
  const pcd = flange.pcd || od * 0.75;
  const boltDia = flange.dia_bolt || 15;
  const nBolt = flange.no_bolt || 4;

  // WN flange geometry:
  //   thickness      — actual flange body thickness incl raised face
  //   thickness_wn   — total flange + hub length (used to derive hub-only length)
  //   pipe_dia       — pipe OD at the far end of the hub
  const bodyMm = flange.thickness || od * 0.1;
  const totalMm = flange.thickness_wn || bodyMm * 3.5;
  const hubMm = Math.max(bodyMm * 1.2, totalMm - bodyMm);
  const thickness = bodyMm / od;                 // flange body only (thin!)
  const hubLen = hubMm / od;                     // hub only, sits below body
  const pipeOD = (flange.pipe_dia || rfDia * 0.72) / od;
  const hubRootR = Math.min(rfDia / od * 0.95, pipeOD * 1.55);

  const rOut = 1.0;
  const rRF = rfDia / od;
  const rPCD = pcd / od;
  const rBolt = boltDia / od / 2;
  const rBore = pipeOD * 0.88;

  const anyCoverageOK = [face1Coverage, face2Coverage, face3Coverage].some((c) => c >= 180);

  // Sector arc geometry helper
  const arcMesh = (i, nSectors, color, rInner, rOuter, y, opacity = 0.95) => {
    const step = (2 * Math.PI) / nSectors;
    const a1 = i * step - Math.PI / 2;
    const a2 = (i + 1) * step - Math.PI / 2;
    const shape = new THREE.Shape();
    shape.moveTo(Math.cos(a1) * rOuter, Math.sin(a1) * rOuter);
    shape.absarc(0, 0, rOuter, a1, a2, false);
    shape.lineTo(Math.cos(a2) * rInner, Math.sin(a2) * rInner);
    shape.absarc(0, 0, rInner, a2, a1, true);
    shape.closePath();
    const geo = new THREE.ShapeGeometry(shape, 8);
    return (
      <mesh
        key={`${nSectors}-${i}-${y}-${color}`}
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, y, 0]}
      >
        <primitive object={geo} attach="geometry" />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={opacity}
          side={THREE.DoubleSide}
          toneMapped={false}
        />
      </mesh>
    );
  };

  // Face plane Y positions (front → back) — expanded separation so points
  // for different faces are clearly distinct in the 3D scene.
  const yFace1 = thickness / 2 + 0.06;   // raised face — well above the body
  const yFace2 = 0.0;                     // mid-body / middle
  const yFace3 = -thickness / 2 - 0.06;  // back face — well below body

  const face1OK = face1Coverage >= 180;
  const face2OK = face2Coverage >= 180;
  const face3OK = face3Coverage >= 180;
  const boltOK = boltCoverage >= 180;

  return (
    <group ref={group} rotation={[0, 0, 0]}>
      {/* Weld-neck hub (frustum from just under the flange down to pipe OD) */}
      <mesh
        position={[0, -thickness / 2 - hubLen / 2, 0]}
        castShadow
        receiveShadow
      >
        <cylinderGeometry args={[hubRootR, pipeOD, hubLen, 64, 1, false]} />
        <meshStandardMaterial
          color="#4a5568"
          metalness={0.85}
          roughness={0.4}
          envMapIntensity={1.1}
        />
      </mesh>

      {/* Inner bore hole through the hub (dark ring interior) */}
      <mesh position={[0, -thickness / 2 - hubLen / 2, 0]}>
        <cylinderGeometry args={[rBore, rBore * 0.95, hubLen + 0.01, 48, 1, true]} />
        <meshStandardMaterial
          color="#111418"
          metalness={0.3}
          roughness={0.9}
          side={THREE.BackSide}
        />
      </mesh>

      {/* Pipe stub emerging from the hub */}
      <mesh position={[0, -thickness / 2 - hubLen - 0.08, 0]}>
        <cylinderGeometry args={[pipeOD, pipeOD, 0.18, 48]} />
        <meshStandardMaterial color="#3b4252" metalness={0.75} roughness={0.5} />
      </mesh>

      {/* Main flange body (thin disc) */}
      <mesh castShadow receiveShadow>
        <cylinderGeometry args={[rOut, rOut, thickness, 96, 1, false]} />
        <meshStandardMaterial
          color={anyCoverageOK ? "#3d5445" : "#4a5568"}
          metalness={0.85}
          roughness={0.32}
          emissive={anyCoverageOK ? "#0d4a1a" : "#000000"}
          emissiveIntensity={anyCoverageOK ? 0.2 : 0}
          envMapIntensity={1.2}
        />
      </mesh>

      {/* Raised face on top (short lip) */}
      <mesh position={[0, thickness / 2 + 0.008, 0]} castShadow>
        <cylinderGeometry args={[rRF, rRF, 0.016, 72]} />
        <meshStandardMaterial
          color="#5a6478"
          metalness={0.85}
          roughness={0.28}
          envMapIntensity={1.1}
        />
      </mesh>

      {/* Bore on the raised face — dark inner ring so you see the hole */}
      <mesh position={[0, thickness / 2 + 0.017, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0, rBore, 48]} />
        <meshBasicMaterial color="#0a0d13" side={THREE.DoubleSide} />
      </mesh>

      {/* RTJ groove (if applicable) */}
      {flange.gasket === "RTJ" && (
        <mesh position={[0, thickness / 2 + 0.018, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[rRF * 0.72, rRF * 0.78, 96]} />
          <meshBasicMaterial color="#ffb000" side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
      )}

      {/* Bolt holes — just dark holes drilled through the flange, no bolts */}
      {Array.from({ length: nBolt }).map((_, i) => {
        const a = i * (2 * Math.PI / nBolt) - Math.PI / 2;
        const x = Math.cos(a) * rPCD;
        const z = Math.sin(a) * rPCD;
        return (
          <group key={i} position={[x, 0, z]}>
            {/* Dark hole interior — visible from above and below */}
            <mesh position={[0, thickness / 2 + 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <ringGeometry args={[0, rBolt, 20]} />
              <meshBasicMaterial color="#050607" side={THREE.DoubleSide} />
            </mesh>
            <mesh position={[0, -thickness / 2 - 0.005, 0]} rotation={[Math.PI / 2, 0, 0]}>
              <ringGeometry args={[0, rBolt, 20]} />
              <meshBasicMaterial color="#050607" side={THREE.DoubleSide} />
            </mesh>
            {/* Barrel of the hole */}
            <mesh>
              <cylinderGeometry args={[rBolt, rBolt, thickness + 0.02, 20, 1, true]} />
              <meshStandardMaterial
                color="#1a1d24"
                metalness={0.4}
                roughness={0.9}
                side={THREE.BackSide}
              />
            </mesh>
          </group>
        );
      })}

      {/* PCD indicator ring on top */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, thickness / 2 + 0.011, 0]}>
        <ringGeometry args={[rPCD - 0.003, rPCD + 0.003, 128]} />
        <meshBasicMaterial color="#5a6478" transparent opacity={0.6} side={THREE.DoubleSide} />
      </mesh>

      {/* Face 1 coverage — on the raised face top */}
      {face1Sectors.map((on, i) =>
        on
          ? arcMesh(i, face1Sectors.length,
                    face1OK ? "#00ff41" : "#ffb000",
                    rOut * 0.94, rOut - 0.005,
                    yFace1 + 0.006)
          : null
      )}

      {/* Face 2 coverage — floating slightly inside the body mid-height */}
      {face2Sectors.map((on, i) =>
        on
          ? arcMesh(i, face2Sectors.length,
                    face2OK ? "#00ff41" : "#ffb000",
                    rOut * 0.96, rOut + 0.008,
                    yFace2, 0.75)
          : null
      )}

      {/* Face 3 coverage — on the back face bottom */}
      {face3Sectors.map((on, i) =>
        on
          ? arcMesh(i, face3Sectors.length,
                    face3OK ? "#00ff41" : "#ffb000",
                    rOut * 0.94, rOut - 0.005,
                    yFace3 - 0.006)
          : null
      )}

      {/* PCD coverage on the bolt-circle ring */}
      {boltSectors.map((on, i) =>
        on
          ? arcMesh(i, boltSectors.length,
                    boltOK ? "#00ff41" : "#ffb000",
                    rPCD - 0.025, rPCD + 0.025,
                    yFace1 + 0.008)
          : null
      )}

      {/* Measured points — glowing spheres, one color per kind. Points for
          each kind sit exactly on their own face plane, well separated. */}
      <PointCloud pts={face1Points} yOffset={yFace1} color={KIND_COLORS.face1} radiusR={rRF} od={od} />
      <PointCloud pts={face2Points} yOffset={yFace2} color={KIND_COLORS.face2} radiusR={rOut} od={od} outward />
      <PointCloud pts={face3Points} yOffset={yFace3} color={KIND_COLORS.face3} radiusR={rOut} od={od} />
      <PointCloud pts={boltPoints} yOffset={yFace1 - 0.02} color={KIND_COLORS.bolt} radiusR={rPCD} od={od} />

      {/* Global glow halo when finalize eligible */}
      {face1OK && face2OK && boltOK && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, yFace1 + 0.02, 0]}>
          <ringGeometry args={[rOut + 0.01, rOut + 0.09, 128]} />
          <meshBasicMaterial color="#00ff41" transparent opacity={0.35} side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
      )}
    </group>
  );
};

// Render a small point-cloud on the appropriate ring position
const PointCloud = ({ pts, yOffset, color, radiusR, od, outward = false }) => {
  const scaled = useMemo(() => {
    if (pts.length === 0) return [];
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    const cz = pts.reduce((s, p) => s + p.z, 0) / pts.length;
    const scale = 1 / (od / 2000);
    return pts.map((p) => {
      const dx = (p.y - cy) * scale;
      const dz = (p.z - cz) * scale;
      // Snap to the correct ring radius so points always sit on the geometry
      const r = Math.hypot(dx, dz) || 1;
      const rTarget = radiusR + (outward ? 0.015 : 0);
      return {
        x: (dx / r) * rTarget,
        z: (dz / r) * rTarget,
      };
    });
  }, [pts, od, radiusR, outward]);
  return (
    <>
      {scaled.map((p, i) => (
        <mesh key={i} position={[p.x, yOffset, p.z]}>
          <sphereGeometry args={[0.024, 16, 16]} />
          <meshBasicMaterial color={color} toneMapped={false} />
        </mesh>
      ))}
    </>
  );
};
